import React from 'react';
import { GraduationCap, LifeBuoy, BadgeCheck, ArrowUpRight } from 'lucide-react';
import { Reveal, SectionEyebrow, SectionTitle, SectionBody, Container } from './Primitives';
import { useLanguage } from '@/context/LanguageContext';
import { SITE } from '@/constants/testIds';

const ABOUT_IMAGE =
  'https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBoZWFsdGhjYXJlfGVufDB8fHx8MTc4MTI4MDg5N3ww&ixlib=rb-4.1.0&q=85';

const icons = [GraduationCap, LifeBuoy, BadgeCheck];

const Solution = () => {
  const { t } = useLanguage();
  return (
    <section
      id="solution"
      data-testid={SITE.sectionSolution}
      className="relative py-20 lg:py-32 bg-white"
    >
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionEyebrow>{t.solution.eyebrow}</SectionEyebrow>
            </Reveal>
            <Reveal delay={0.05}>
              <SectionTitle className="mt-4">{t.solution.title}</SectionTitle>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <SectionBody>{t.solution.body}</SectionBody>
            </Reveal>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Dominant card with image */}
          <Reveal className="lg:col-span-6" delay={0}>
            <article
              data-testid={SITE.solutionCard}
              className="h-full relative overflow-hidden rounded-3xl border border-[rgba(0,166,81,0.15)] bg-[#0B0F0C] text-white p-8 lg:p-10 flex flex-col justify-between min-h-[480px]"
            >
              <img
                src={ABOUT_IMAGE}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-25"
              />
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(160deg, rgba(11,15,12,0.85) 0%, rgba(11,15,12,0.65) 50%, rgba(0,166,81,0.45) 120%)',
                }}
              />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-[#22C55E]/15 flex items-center justify-center backdrop-blur">
                  <GraduationCap size={22} className="text-[#22C55E]" />
                </div>
                <div className="mt-6 text-xs font-bold tracking-[0.22em] uppercase text-[#22C55E]">
                  01 · Formation
                </div>
                <h3 className="mt-3 font-['Outfit'] text-2xl lg:text-3xl font-semibold leading-snug max-w-md">
                  {t.solution.cards[0].title}
                </h3>
                <p className="mt-4 text-white/75 max-w-md leading-relaxed">
                  {t.solution.cards[0].desc}
                </p>
              </div>
              <div className="relative mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#22C55E]">
                E-Gestionnaire
                <ArrowUpRight size={16} />
              </div>
            </article>
          </Reveal>

          {/* Two smaller cards */}
          <div className="lg:col-span-6 grid grid-cols-1 gap-5">
            {t.solution.cards.slice(1).map((card, i) => {
              const Icon = icons[i + 1];
              const num = i === 0 ? '02' : '03';
              return (
                <Reveal key={card.title} delay={(i + 1) * 0.08}>
                  <article
                    data-testid={SITE.solutionCard}
                    className="h-full bg-white rounded-3xl p-8 lg:p-10 border border-[rgba(0,166,81,0.12)] hover:border-[rgba(0,166,81,0.35)] transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_18px_40px_-10px_rgba(0,166,81,0.18)] hover:-translate-y-1 group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-xl bg-[#F7F8F5] flex items-center justify-center group-hover:bg-[#00A651]/10 transition-colors">
                        <Icon size={22} className="text-[#00A651]" />
                      </div>
                      <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#00A651]/60">
                        {num}
                      </div>
                    </div>
                    <h3 className="mt-5 font-['Outfit'] text-xl lg:text-2xl font-semibold text-[#0B0F0C] leading-snug">
                      {card.title}
                    </h3>
                    <p className="mt-3 text-[#5C5C5C] leading-relaxed">{card.desc}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Solution;
